/**
 * Zuschnitt fuer Plaene aus Bildern (docs/planverortung-vertrag.md, P2).
 *
 * Vorlage ist der ImageCropper; der bleibt unveraendert, weil er auch Fotos
 * (3:4) zuschneidet. Hier dazu: Planname, Rahmen „A4 quer“ (√2, fuellt die
 * A4-quer-Berichtsseite genau) / „Frei“ / „Ganzes Bild“ und 90°-Drehen fuer
 * Hochformat-Plaene.
 */
import React, { useRef, useState } from 'react';
import type { JSX } from 'react';
import ReactCrop, { type Crop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Check, Loader2, Maximize, RectangleHorizontal, RotateCw, SquareDashed } from 'lucide-react';
import { Button } from '../Button';
import { zielMasse } from '../imageCropperMasse';
import { A4_QUER, flaecheBegrenzen, groessterRahmenProzent } from './planFormat';

export interface PlanBildZuschnittProps {
    /** Bild als dataURL. */
    image: string;
    /** Vorschlag fuer den Planname (z. B. aus dem Dateinamen). */
    startName: string;
    /** JPEG-dataURL (lange Kante hoechstens 4096 px) und Planname (getrimmt, nie leer). */
    onSave: (base64: string, name: string) => void;
    onCancel: () => void;
}

type RahmenArt = 'a4' | 'frei';

/** Ganzes Bild als Ausschnitt (freies Format). */
const GANZES_BILD: Crop = { unit: '%', x: 0, y: 0, width: 100, height: 100 };
const EXPORT_QUALITAET = 0.9;

export function PlanBildZuschnitt({ image, startName, onSave, onCancel }: PlanBildZuschnittProps): JSX.Element {
    /** Aktuelle Fassung des Bilds (nach dem Drehen eine neue dataURL). */
    const [quelle, setQuelle] = useState(image);
    const [crop, setCrop] = useState<Crop>();
    const [rahmenArt, setRahmenArt] = useState<RahmenArt>('a4');
    const [name, setName] = useState(startName);
    const [dreht, setDreht] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    /** Hinweis in der Fussleiste (z. B. Drehen fehlgeschlagen) — kein Browser-Dialog. */
    const [hinweis, setHinweis] = useState<string | null>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    const nameOk = name.trim().length > 0;

    /** Rahmen neu setzen: A4 quer = groesster mittiger √2-Rahmen, frei = ganzes Bild. */
    const rahmenSetzen = (art: RahmenArt, bild: HTMLImageElement | null = imgRef.current) => {
        if (!bild || !bild.naturalWidth || !bild.naturalHeight) {
            setCrop(undefined);
            return;
        }
        if (art === 'a4') {
            const r = groessterRahmenProzent(bild.naturalWidth, bild.naturalHeight, A4_QUER);
            setCrop({ unit: '%', x: r.x, y: r.y, width: r.w, height: r.h });
        } else {
            setCrop(GANZES_BILD);
        }
    };

    const rahmenArtWaehlen = (art: RahmenArt) => {
        setRahmenArt(art);
        rahmenSetzen(art);
    };

    const ganzesBild = () => {
        setRahmenArt('frei');
        setCrop(GANZES_BILD);
    };

    /**
     * 90° im Uhrzeigersinn: Bild per Canvas drehen (weisser Grund — ein
     * transparentes PNG wuerde als JPEG sonst schwarz), Rahmen danach neu.
     * Riesige Fotos werden dabei auf eine Canvas-Flaeche begrenzt, die auch
     * iOS noch zeichnet.
     */
    const drehen = () => {
        const bild = imgRef.current;
        if (!bild || dreht || !bild.naturalWidth || !bild.naturalHeight) return;
        setDreht(true);
        setHinweis(null);
        // Kurz warten, damit der Wartekreis erscheint — toDataURL blockiert.
        window.setTimeout(() => {
            const canvas = document.createElement('canvas');
            try {
                const m = flaecheBegrenzen(bild.naturalWidth, bild.naturalHeight);
                canvas.width = m.hoehe;
                canvas.height = m.breite;
                const ctx = canvas.getContext('2d');
                if (!ctx) throw new Error('Kein Canvas-Kontext');
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.translate(canvas.width, 0);
                ctx.rotate(Math.PI / 2);
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(bild, 0, 0, m.breite, m.hoehe);
                const gedreht = canvas.toDataURL('image/jpeg', 0.95);
                setCrop(undefined);
                setQuelle(gedreht);
            } catch (e) {
                console.error('Plan drehen fehlgeschlagen', e);
                setHinweis('Das Bild konnte nicht gedreht werden. Bitte erneut versuchen.');
            } finally {
                canvas.width = 0;
                canvas.height = 0;
                setDreht(false);
            }
        }, 30);
    };

    const handleSave = () => {
        const bild = imgRef.current;
        // Der aktuelle Rahmen gilt — auch ohne Ziehen.
        if (!nameOk || dreht || !crop || !bild || !bild.width || !bild.height) return;
        const px = convertToPixelCrop(crop, bild.width, bild.height);
        if (px.width <= 0 || px.height <= 0) return;

        const scaleX = bild.naturalWidth / bild.width;
        const scaleY = bild.naturalHeight / bild.height;
        const cropX = px.x * scaleX;
        const cropY = px.y * scaleY;
        const cropWidth = px.width * scaleX;
        const cropHeight = px.height * scaleY;
        const ziel = zielMasse(cropWidth, cropHeight);

        const canvas = document.createElement('canvas');
        canvas.width = ziel.breite;
        canvas.height = ziel.hoehe;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, ziel.breite, ziel.hoehe);
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bild, cropX, cropY, cropWidth, cropHeight, 0, 0, ziel.breite, ziel.hoehe);

        const dataUrl = canvas.toDataURL('image/jpeg', EXPORT_QUALITAET);
        canvas.width = 0;
        canvas.height = 0;
        onSave(dataUrl, name.trim());
    };

    const knopf = (aktiv: boolean) =>
        `flex items-center gap-2 px-3 min-h-[40px] rounded-md text-sm transition-all ${aktiv ? 'bg-blue-600 text-white shadow-md font-bold' : 'text-slate-300 hover:bg-slate-600'}`;

    return (
        <div className="fixed inset-0 z-[140] bg-slate-900 flex flex-col animate-fade-in text-white">
            {/* Werkzeugleiste: darf am Handy waagrecht scrollen. */}
            <div className="bg-slate-800 p-3 md:p-4 flex items-center shrink-0 shadow-md z-10 overflow-x-auto">
                <div className="flex items-center gap-3 md:gap-4">
                    <div className="hidden md:block min-w-0">
                        <h3 className="font-bold text-lg">Plan zuschneiden</h3>
                        <p className="text-xs text-slate-400">
                            {rahmenArt === 'a4'
                                ? 'Rahmen „A4 quer“ füllt die Berichtsseite genau. Rahmen verschieben oder an den Ecken ziehen.'
                                : 'Rahmen an den Ecken ziehen — Hoch- oder Querformat, wie der Plan es braucht.'}
                        </p>
                    </div>

                    <div className="flex bg-slate-700 rounded-lg p-1 gap-1 shrink-0" role="group" aria-label="Rahmenformat">
                        <button
                            type="button"
                            onClick={() => rahmenArtWaehlen('a4')}
                            className={knopf(rahmenArt === 'a4')}
                            aria-pressed={rahmenArt === 'a4'}
                            title="Rahmen im Format A4 quer (füllt die Berichtsseite)"
                        >
                            <RectangleHorizontal className="w-4 h-4" /> <span className="whitespace-nowrap">A4 quer</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => rahmenArtWaehlen('frei')}
                            className={knopf(rahmenArt === 'frei')}
                            aria-pressed={rahmenArt === 'frei'}
                            title="Rahmen frei ziehen"
                        >
                            <SquareDashed className="w-4 h-4" /> <span>Frei</span>
                        </button>
                    </div>

                    <div className="flex bg-slate-700 rounded-lg p-1 gap-1 shrink-0">
                        <button
                            type="button"
                            onClick={ganzesBild}
                            className={knopf(false)}
                            title="Ganzes Bild übernehmen"
                        >
                            <Maximize className="w-4 h-4" /> <span className="whitespace-nowrap">Ganzes Bild</span>
                        </button>
                        <button
                            type="button"
                            onClick={drehen}
                            disabled={dreht || !!fehler}
                            className={`${knopf(false)} disabled:opacity-40`}
                            title="90° drehen (Hochformat-Plan quer legen)"
                            aria-label="90° drehen"
                        >
                            {dreht ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
                            <span className="whitespace-nowrap">Drehen</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex-1 min-h-0 bg-black p-4 flex overflow-auto touch-none">
                {fehler ? (
                    <p className="m-auto max-w-sm text-center text-sm text-amber-200">{fehler}</p>
                ) : (
                    <div className="m-auto">
                        <ReactCrop
                            crop={crop}
                            onChange={(_, percentCrop) => setCrop(percentCrop)}
                            aspect={rahmenArt === 'a4' ? A4_QUER : undefined}
                            keepSelection
                            disabled={dreht}
                        >
                            <img
                                ref={imgRef}
                                alt="Plan"
                                src={quelle}
                                onLoad={(e) => { setFehler(null); rahmenSetzen(rahmenArt, e.currentTarget); }}
                                onError={() => setFehler('Dieses Bild kann hier nicht angezeigt werden (Format?). Bitte als JPG, PNG oder PDF hochladen.')}
                                style={{ maxHeight: 'max(12rem, calc(100dvh - 17rem))', maxWidth: '100%', objectFit: 'contain' }}
                                crossOrigin="anonymous"
                            />
                        </ReactCrop>
                    </div>
                )}
            </div>

            {/* Fussleiste wie im PdfCropper: Name + Aktionen immer sichtbar, unten. */}
            <div className="bg-slate-800 border-t border-slate-700 p-3 md:p-4 shrink-0 pb-safe">
                {hinweis && (
                    <p role="alert" className="mb-3 rounded-lg bg-red-900/60 border border-red-700 px-3 py-2 text-sm text-red-100">{hinweis}</p>
                )}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <label className="block flex-1 min-w-0">
                        <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Planname</span>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onFocus={(e) => e.currentTarget.select()}
                            placeholder="z. B. Grundriss EG"
                            maxLength={120}
                            enterKeyHint="done"
                            aria-invalid={!nameOk}
                            className={`w-full h-11 rounded-lg border bg-slate-900 px-3 text-sm text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500 ${nameOk ? 'border-slate-600' : 'border-amber-400'}`}
                        />
                        {!nameOk && <span className="mt-1 block text-[11px] text-amber-300">Bitte einen Namen für den Plan eingeben.</span>}
                    </label>
                    <div className="grid grid-cols-2 gap-3 sm:flex sm:shrink-0">
                        <Button variant="ghost" onClick={onCancel} className="text-white hover:bg-slate-700 w-full sm:w-auto justify-center min-h-[44px]">Abbrechen</Button>
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            disabled={!nameOk || !crop || dreht || !!fehler}
                            icon={<Check className="w-4 h-4" />}
                            className="w-full sm:w-auto justify-center min-h-[44px]"
                        >
                            Übernehmen
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
